import React, { useState, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { HiMagnifyingGlass, HiChevronDown, HiChevronUp } from 'react-icons/hi2';
import { BaseModal } from '../Modal/BaseModal';
import { Button } from '../Button';
import { Input } from '../Input';
import { Badge } from '../Badge';
import { getShortcutsForPlatform, searchShortcuts, getPlatform } from '../../config/shortcuts';

export interface KeyboardShortcutsHelpModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export const KeyboardShortcutsHelpModal: React.FC<KeyboardShortcutsHelpModalProps> = ({
  isOpen,
  onClose
}) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [expandedCategories, setExpandedCategories] = useState<Set<string>>(new Set());
  const platform = getPlatform();
  const shortcuts = useMemo(() => getShortcutsForPlatform(), []);

  const filteredShortcuts = useMemo(() => {
    if (!searchQuery.trim()) {
      return shortcuts;
    }
    return searchShortcuts(searchQuery);
  }, [shortcuts, searchQuery]);

  const toggleCategory = (categoryName: string) => {
    setExpandedCategories(prev => {
      const newSet = new Set(prev);
      if (newSet.has(categoryName)) {
        newSet.delete(categoryName);
      } else {
        newSet.add(categoryName);
      }
      return newSet;
    });
  };

  const isCategoryExpanded = (categoryName: string) => {
    return expandedCategories.has(categoryName);
  };

  const formatKeys = (keys: string): string => {
    const modifierKey = platform === 'mac' ? '⌘' : 'Ctrl';
    return keys
      .replace(/ctrl/g, modifierKey)
      .replace(/cmd/g, '⌘')
      .replace(/alt/g, '⌥')
      .replace(/shift/g, '⇧')
      .replace(/escape/g, 'Esc')
      .replace(/enter/g, 'Enter')
      .replace(/tab/g, 'Tab');
  };

  // Check if shortcut is a sequence (has space in it, not just +)
  const isSequenceShortcut = (keys: string): boolean => {
    return keys.includes(' ') && !keys.match(/^[a-z]$/);
  };

  // Render keyboard shortcut with proper visual separation
  const renderShortcut = (keys: string) => {
    const formattedKeys = formatKeys(keys);
    
    if (isSequenceShortcut(formattedKeys)) {
      // Sequence shortcut: split by space (e.g., "Ctrl+D L" -> ["Ctrl+D", "L"])
      const parts = formattedKeys.split(' ').filter(p => p.trim());
      
      return (
        <div className="flex items-center gap-2">
          {parts.map((part, partIndex) => (
            <React.Fragment key={partIndex}>
              {/* Each part of the sequence */}
              <div className="flex items-center gap-1">
                {part.split('+').map((key, keyIndex) => (
                  <React.Fragment key={keyIndex}>
                    <kbd className="px-3 py-2 text-sm font-mono bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg shadow-sm text-gray-800 dark:text-gray-200 min-w-[2.5rem] text-center">
                      {key.trim()}
                    </kbd>
                    {keyIndex < part.split('+').length - 1 && (
                      <span className="text-gray-400 dark:text-gray-500 text-sm font-bold">+</span>
                    )}
                  </React.Fragment>
                ))}
              </div>
              
              {/* Arrow between sequence parts */}
              {partIndex < parts.length - 1 && (
                <div className="flex flex-col items-center gap-1">
                  <span className="text-blue-500 dark:text-blue-400 text-xl font-bold">→</span>
                  <span className="text-xs text-gray-500 dark:text-gray-400 whitespace-nowrap">depois</span>
                </div>
              )}
            </React.Fragment>
          ))}
        </div>
      );
    } else {
      // Regular shortcut: all keys pressed together
      return (
        <div className="flex items-center gap-1">
          {formattedKeys.split('+').map((key, index) => (
            <React.Fragment key={index}>
              <kbd className="px-3 py-2 text-sm font-mono bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg shadow-sm text-gray-800 dark:text-gray-200 min-w-[2.5rem] text-center">
                {key.trim()}
              </kbd>
              {index < formattedKeys.split('+').length - 1 && (
                <span className="text-gray-400 dark:text-gray-500 text-sm font-bold">+</span>
              )}
            </React.Fragment>
          ))}
        </div>
      );
    }
  };

  const getCategoryColor = (category: string): string => {
    const colors = {
      navigation: 'bg-blue-100 text-blue-800 dark:bg-blue-900/20 dark:text-blue-400',
      sectors: 'bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-400',
      demands: 'bg-purple-100 text-purple-800 dark:bg-purple-900/20 dark:text-purple-400',
      modals: 'bg-orange-100 text-orange-800 dark:bg-orange-900/20 dark:text-orange-400',
      general: 'bg-gray-100 text-gray-800 dark:bg-gray-900/20 dark:text-gray-400'
    };
    return colors[category as keyof typeof colors] || colors.general;
  };

  const getCategoryIcon = (category: string): string => {
    const icons = {
      navigation: '🧭',
      sectors: '🏢',
      demands: '📋',
      modals: '🪟',
      general: '⚙️'
    };
    return icons[category as keyof typeof icons] || icons.general;
  };

  return (
    <BaseModal
      isOpen={isOpen}
      onClose={onClose}
      title="Atalhos de Teclado"
      size="xl"
      className="max-h-[90vh] w-[90vw] max-w-6xl"
    >
      <div className="flex flex-col h-full max-h-[80vh] p-6">
        {/* Search */}
        <div className="mb-6 flex-shrink-0">
          <div className="relative">
            <HiMagnifyingGlass className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
            <Input
              placeholder="Buscar atalhos..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-12 h-12 text-lg"
            />
          </div>
        </div>

        {/* Legend - compacta */}
        <div className="mb-4 px-4 py-2 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-800 flex-shrink-0">
          <div className="flex items-center justify-between gap-4 text-xs text-blue-800 dark:text-blue-200">
            <div className="flex items-center gap-2">
              <span className="font-semibold">📖</span>
              <kbd className="px-1.5 py-0.5 text-xs bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded">Ctrl+S</kbd>
              <span>= juntas</span>
            </div>
            <div className="flex items-center gap-2">
              <kbd className="px-1.5 py-0.5 text-xs bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded">Ctrl+D</kbd>
              <span className="text-blue-500 font-bold">→</span>
              <kbd className="px-1.5 py-0.5 text-xs bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded">L</kbd>
              <span>= sequência</span>
            </div>
          </div>
        </div>

        {/* Content - Accordion style */}
        <div className="flex-1 overflow-y-auto space-y-3 pr-2">
          {filteredShortcuts.length === 0 ? (
            <div className="text-center py-12 text-gray-500 dark:text-gray-400">
              <div className="text-6xl mb-4">🔍</div>
              <p className="text-lg">Nenhum atalho encontrado para "{searchQuery}"</p>
            </div>
          ) : (
            filteredShortcuts.map((category) => (
              <div 
                key={category.name} 
                className="border border-gray-200 dark:border-gray-700 rounded-xl overflow-hidden bg-white dark:bg-gray-800"
              >
                {/* Category Header - Clickable */}
                <button
                  onClick={() => toggleCategory(category.name)}
                  className="w-full px-5 py-4 flex items-center justify-between hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors"
                >
                  <div className="flex items-center gap-3">
                    <span className="text-2xl">{getCategoryIcon(category.name.toLowerCase())}</span>
                    <h3 className="text-lg font-bold text-gray-900 dark:text-gray-100">
                      {category.name}
                    </h3>
                    <Badge variant="info" style="ghost" className={getCategoryColor(category.name.toLowerCase())}>
                      {category.shortcuts.length}
                    </Badge>
                  </div>
                  
                  <div className="flex items-center gap-2">
                    {isCategoryExpanded(category.name) ? (
                      <HiChevronUp className="w-5 h-5 text-gray-500 dark:text-gray-400" />
                    ) : (
                      <HiChevronDown className="w-5 h-5 text-gray-500 dark:text-gray-400" />
                    )}
                  </div>
                </button>

                {/* Category Content - Collapsible */}
                <AnimatePresence>
                  {isCategoryExpanded(category.name) && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: 'auto', opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.2 }}
                      className="border-t border-gray-200 dark:border-gray-700"
                    >
                      <div className="p-4 space-y-2">
                        <p className="text-sm text-gray-600 dark:text-gray-400 mb-3">
                          {category.description}
                        </p>

                        {category.shortcuts.map((shortcut) => (
                          <motion.div
                            key={shortcut.keys}
                            initial={{ opacity: 0, y: 10 }}
                            animate={{ opacity: 1, y: 0 }}
                            className="flex items-center justify-between p-3 rounded-lg bg-gray-50 dark:bg-gray-700/30 hover:bg-gray-100 dark:hover:bg-gray-700 transition-all duration-200"
                          >
                            <div className="flex-1">
                              <p className="text-sm font-medium text-gray-900 dark:text-gray-100">
                                {shortcut.description}
                              </p>
                              {isSequenceShortcut(formatKeys(shortcut.keys)) && (
                                <p className="text-xs text-blue-600 dark:text-blue-400 mt-1 flex items-center gap-1">
                                  <span>💡</span>
                                  <span>Pressione as teclas em sequência (não ao mesmo tempo)</span>
                                </p>
                              )}
                            </div>
                            
                            <div className="flex items-center gap-2 ml-4">
                              {renderShortcut(shortcut.keys)}
                            </div>
                          </motion.div>
                        ))}
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            ))
          )}
        </div>

        {/* Bottom Action Bar */}
        <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-700 flex items-center justify-between flex-shrink-0">
          <div className="text-sm text-gray-500 dark:text-gray-400">
            <span className="font-medium">Plataforma:</span> {platform === 'mac' ? 'macOS' : platform === 'windows' ? 'Windows' : 'Outro'}
          </div>
          <Button variant="outline" onClick={onClose} className="px-6">
            Fechar
          </Button>
        </div>
      </div>
    </BaseModal>
  );
};
